import { useState } from 'react';
import { Button, Dialog, DialogContent, Stack, Typography, TextField } from '@mui/material';
import api from 'api/api';
import serverCall from 'api/serverCall';
import Avatar from 'components/@extended/Avatar';
import { PopupTransition } from 'components/@extended/Transitions';
import { Edit } from 'iconsax-react';
import { useDispatch } from 'react-redux';
import { openSnackbar } from 'store/reducers/snackbar';

interface Props {
  state: { open: boolean; orderId: string; userId: string; currentQty: number };
  setState: (val: { open: boolean; orderId: string; userId: string; currentQty: number }) => void;
  refetchSimulation: () => void;
}

export default function ChangeOrderQuantityModal({ state, setState, refetchSimulation }: Props) {
  const dispatch = useDispatch();
  const [quantity, setQuantity] = useState(state.currentQty?.toString() ?? '');
  const [loading, setLoading] = useState(false);

  const handleChangeQuantity = (orderId: string) => {
    const qtyNumber = Number(quantity);

    if (!orderId || qtyNumber <= 0) {
      dispatch(
        openSnackbar({
          open: true,
          close: true,
          message: `Please enter a valid quantity.`,
          variant: 'alert',
          alert: { color: 'warning' },
          anchorOrigin: { vertical: 'top', horizontal: 'center' }
        })
      );
      return;
    }

    setLoading(true);

    serverCall.put(api.changeQuantity(orderId, qtyNumber), {}, (response, error) => {
      setLoading(false);
      if (error) {
        dispatch(
          openSnackbar({
            open: true,
            close: true,
            message: error?.message ?? `Failed to update order quantity.`,
            variant: 'alert',
            alert: { color: 'error' },
            anchorOrigin: { vertical: 'top', horizontal: 'center' }
          })
        );
      } else {
        refetchSimulation();
        dispatch(
          openSnackbar({
            open: true,
            close: true,
            message: `Quantity updated successfully for order ${orderId}.`,
            variant: 'alert',
            alert: { color: 'success' },
            anchorOrigin: { vertical: 'top', horizontal: 'center' }
          })
        );
        setState({ open: false, orderId: '', userId: '', currentQty: 0 });
      }
    });
  };

  return (
    <Dialog
      open={state.open}
      onClose={() => setState({ open: false, orderId: '', userId: '', currentQty: 0 })}
      keepMounted
      fullWidth
      TransitionComponent={PopupTransition}
      maxWidth="xs"
      aria-labelledby="change-order-quantity-title"
      aria-describedby="change-order-quantity-description"
      sx={{ zIndex: 9998 }}
    >
      <DialogContent sx={{ mt: 2, my: 1 }}>
        <Stack alignItems="center" spacing={3.5}>
          <Avatar color="primary" sx={{ width: 72, height: 72, fontSize: '1.75rem' }}>
            <Edit variant="Bold" />
          </Avatar>

          <Stack spacing={2} sx={{ width: '100%' }}>
            <Typography variant="h4" align="center">
              Change Order Quantity
            </Typography>
            <TextField
              fullWidth
              label="New Quantity"
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              inputProps={{ min: 1 }}
            />
          </Stack>

          <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
            <Button
              fullWidth
              onClick={() => setState({ open: false, orderId: '', userId: '', currentQty: 0 })}
              color="secondary"
              variant="outlined"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              color="primary"
              variant="contained"
              onClick={() => handleChangeQuantity(state.orderId)}
              disabled={loading}
              autoFocus
            >
              {loading ? 'Saving...' : 'Update'}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
